import {
	addDays,
	formatPaise,
	type ImageRef,
	instantOfLocalTime,
	localParts,
	type Offer,
	type OfferAppliesTo,
	type OfferDiscountType,
	type OfferKind,
	parseRupees,
	type UpsertOfferInput,
	upsertOfferInputSchema,
	uuidv7,
} from "@luckymenu/core";
import { paiseToRupees } from "../menu/form-errors";

/**
 * The offer form (P9-T09) as both apps edit it, in three steps: what kind,
 * what it applies to, and when. Text as typed: percentages ("10", "12.5")
 * and rupees; dates as the owner picks them, in the restaurant's time zone.
 * `offerDraftToInput` turns it into `staff.promotions.upsert`'s input.
 */

export type OfferDraft = {
	id: string;
	/** Step 1: what kind. */
	kind: OfferKind;
	discountType: OfferDiscountType;
	/** A percentage or rupees, as typed. */
	value: string;
	/** Percentages only: the most it takes off, in rupees. Empty: no cap. */
	maxDiscount: string;
	/** Bill offers and coupons: the bill must reach this, in rupees. Empty: none. */
	minOrder: string;
	/** Coupons only. */
	code: string;
	name: string;
	description: string;
	/** Step 2: what it applies to (item offers). */
	appliesTo: OfferAppliesTo;
	itemIds: string[];
	categoryIds: string[];
	/** Step 3: when. "YYYY-MM-DD" in the restaurant's time zone; empty: open. The end day is included. */
	startDate: string;
	endDate: string;
	/** ISO weekdays, 1 Monday. Empty: every day. */
	daysOfWeek: number[];
	timed: boolean;
	timeFrom: string;
	timeTo: string;
	isActive: boolean;
	showOnMenu: boolean;
	banner: ImageRef | null;
};

export const OFFER_STEPS = ["kind", "target", "when"] as const;
export type OfferStep = (typeof OFFER_STEPS)[number];

export const OFFER_LIMITS = { name: 60, description: 200 } as const;

/** A new time window starts as an afternoon happy hour. */
const DEFAULT_WINDOW = { from: "15:00", to: "18:00" };

export function newOfferDraft(id: string = uuidv7()): OfferDraft {
	return {
		id,
		kind: "item_discount",
		discountType: "percent",
		value: "",
		maxDiscount: "",
		minOrder: "",
		code: "",
		name: "",
		description: "",
		appliesTo: "all",
		itemIds: [],
		categoryIds: [],
		startDate: "",
		endDate: "",
		daysOfWeek: [],
		timed: false,
		timeFrom: DEFAULT_WINDOW.from,
		timeTo: DEFAULT_WINDOW.to,
		isActive: true,
		showOnMenu: true,
		banner: null,
	};
}

/** Hundredths as typed text: 1250 → "12.5", 1000 → "10". */
function hundredthsToText(value: number): string {
	const whole = Math.trunc(value / 100);
	const rest = value % 100;
	return rest === 0 ? String(whole) : `${whole}.${String(rest).padStart(2, "0").replace(/0$/, "")}`;
}

export function offerToDraft(offer: Offer, timeZone: string): OfferDraft {
	const endDate = offer.endsAt
		? (() => {
				const end = localParts(new Date(offer.endsAt), timeZone);
				// An end at midnight closes the day before.
				return end.time === "00:00" ? addDays(end.date, -1) : end.date;
			})()
		: "";
	return {
		id: offer.id,
		kind: offer.kind,
		discountType: offer.discountType,
		value:
			offer.discountType === "percent" ? hundredthsToText(offer.value) : paiseToRupees(offer.value),
		maxDiscount: offer.maxDiscountPaise !== null ? paiseToRupees(offer.maxDiscountPaise) : "",
		minOrder: offer.minOrderPaise !== null ? paiseToRupees(offer.minOrderPaise) : "",
		code: offer.code ?? "",
		name: offer.name,
		description: offer.description ?? "",
		appliesTo: offer.appliesTo,
		itemIds: [...offer.itemIds],
		categoryIds: [...offer.categoryIds],
		startDate: offer.startsAt ? localParts(new Date(offer.startsAt), timeZone).date : "",
		endDate,
		daysOfWeek: [...offer.daysOfWeek],
		timed: offer.timeFrom !== null,
		timeFrom: offer.timeFrom ?? DEFAULT_WINDOW.from,
		timeTo: offer.timeTo ?? DEFAULT_WINDOW.to,
		isActive: offer.isActive,
		showOnMenu: offer.showOnMenu,
		banner: offer.banner,
	};
}

// ---------------------------------------------------------------------------
// Edits. Each returns a new draft.
// ---------------------------------------------------------------------------

/** Step 1: a kind keeps only the discount types it allows. */
export function setOfferKind(draft: OfferDraft, kind: OfferKind): OfferDraft {
	const discountType =
		kind !== "item_discount" && draft.discountType === "fixed_price"
			? "percent"
			: draft.discountType;
	return {
		...draft,
		kind,
		discountType,
		...(kind !== "item_discount" ? { appliesTo: "all" as const } : {}),
	};
}

export function toggleOfferDay(draft: OfferDraft, day: number): OfferDraft {
	const days = draft.daysOfWeek.includes(day)
		? draft.daysOfWeek.filter((d) => d !== day)
		: [...draft.daysOfWeek, day].sort((a, b) => a - b);
	// All seven is every day.
	return { ...draft, daysOfWeek: days.length === 7 ? [] : days };
}

export function toggleOfferTarget(
	draft: OfferDraft,
	kind: "items" | "categories",
	id: string,
): OfferDraft {
	const field = kind === "items" ? "itemIds" : "categoryIds";
	const ids = draft[field];
	return { ...draft, [field]: ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id] };
}

// ---------------------------------------------------------------------------
// Validation.
// ---------------------------------------------------------------------------

/** What is wrong with a field. Both apps translate `offers:problems.<problem>`. */
export type OfferFieldProblem =
	| "required"
	| "tooLong"
	| "price"
	| "percent"
	| "code"
	| "taken"
	| "pickTargets"
	| "dateRange"
	| "timeRange"
	| "invalid";

export type OfferErrors = Partial<Record<string, OfferFieldProblem>>;

/** Which step a field is on, to go back to the first step with a problem. */
export function stepOfField(field: string): OfferStep {
	if (["appliesTo", "itemIds", "categoryIds"].includes(field)) return "target";
	if (
		["startDate", "endDate", "startsAt", "endsAt", "timeFrom", "timeTo", "daysOfWeek"].includes(
			field,
		)
	)
		return "when";
	return "kind";
}

/** The problems of one step, so the form can stop before the next. */
export function offerStepErrors(draft: OfferDraft, step: OfferStep, timeZone: string): OfferErrors {
	const result = offerDraftToInput(draft, timeZone);
	if (result.ok) return {};
	return Object.fromEntries(
		Object.entries(result.errors).filter(([field]) => stepOfField(field) === step),
	);
}

/** Rupees as typed to paise; empty is null when optional. */
function money(text: string, optional: boolean): number | null | "required" | "price" {
	if (text.trim() === "") return optional ? null : "required";
	const paise = parseRupees(text);
	return paise === null || paise <= 0 ? "price" : paise;
}

export type OfferFormResult =
	| { ok: true; input: UpsertOfferInput }
	| { ok: false; errors: OfferErrors };

export function offerDraftToInput(draft: OfferDraft, timeZone: string): OfferFormResult {
	const errors: OfferErrors = {};
	const name = draft.name.trim();
	if (name === "") errors.name = "required";
	else if (name.length > OFFER_LIMITS.name) errors.name = "tooLong";
	if (draft.description.trim().length > OFFER_LIMITS.description) errors.description = "tooLong";

	let value = 0;
	if (draft.value.trim() === "") errors.value = "required";
	else {
		const hundredths = parseRupees(draft.value);
		if (draft.discountType === "percent") {
			if (hundredths === null || hundredths < 1 || hundredths > 10000) errors.value = "percent";
			else value = hundredths;
		} else if (hundredths === null || (hundredths <= 0 && draft.discountType === "flat"))
			errors.value = "price";
		else value = hundredths;
	}
	const isItem = draft.kind === "item_discount";
	const max = draft.discountType === "percent" ? money(draft.maxDiscount, true) : null;
	if (typeof max === "string") errors.maxDiscount = max;
	const min = isItem ? null : money(draft.minOrder, true);
	if (typeof min === "string") errors.minOrder = min;
	const code = draft.code.trim().toUpperCase();
	if (draft.kind === "coupon" && !/^[A-Z0-9]{3,20}$/.test(code)) errors.code = "code";

	if (isItem && draft.appliesTo === "items" && draft.itemIds.length === 0)
		errors.itemIds = "pickTargets";
	if (isItem && draft.appliesTo === "categories" && draft.categoryIds.length === 0)
		errors.categoryIds = "pickTargets";

	const date = /^\d{4}-\d{2}-\d{2}$/;
	if (draft.startDate !== "" && !date.test(draft.startDate)) errors.startDate = "invalid";
	if (draft.endDate !== "" && !date.test(draft.endDate)) errors.endDate = "invalid";
	if (draft.startDate !== "" && draft.endDate !== "" && draft.endDate < draft.startDate)
		errors.endDate = "dateRange";
	const time = /^([01]\d|2[0-3]):[0-5]\d$/;
	if (
		draft.timed &&
		(!time.test(draft.timeFrom) || !time.test(draft.timeTo) || draft.timeFrom === draft.timeTo)
	)
		errors.timeTo = "timeRange";
	if (Object.keys(errors).length > 0) return { ok: false, errors };

	const input: UpsertOfferInput = {
		id: draft.id,
		name,
		description: draft.description.trim(),
		kind: draft.kind,
		discountType: draft.discountType,
		value,
		minOrderPaise: typeof min === "number" ? min : null,
		maxDiscountPaise: typeof max === "number" ? max : null,
		code: draft.kind === "coupon" ? code : null,
		appliesTo: isItem ? draft.appliesTo : "all",
		itemIds: isItem && draft.appliesTo === "items" ? draft.itemIds : [],
		categoryIds: isItem && draft.appliesTo === "categories" ? draft.categoryIds : [],
		startsAt:
			draft.startDate !== ""
				? instantOfLocalTime(draft.startDate, "00:00", timeZone).toISOString()
				: null,
		// The end day is included: the offer stops at the next midnight.
		endsAt:
			draft.endDate !== ""
				? instantOfLocalTime(addDays(draft.endDate, 1), "00:00", timeZone).toISOString()
				: null,
		daysOfWeek: draft.daysOfWeek,
		timeFrom: draft.timed ? draft.timeFrom : null,
		timeTo: draft.timed ? draft.timeTo : null,
		isActive: draft.isActive,
		showOnMenu: draft.showOnMenu,
		bannerFileId: draft.banner?.id ?? null,
	};
	const parsed = upsertOfferInputSchema.safeParse(input);
	if (!parsed.success) {
		const zodErrors: OfferErrors = {};
		for (const issue of parsed.error.issues) {
			const key = issue.path.map(String).join(".");
			if (!(key in zodErrors)) zodErrors[key] = "invalid";
		}
		return { ok: false, errors: zodErrors };
	}
	return { ok: true, input };
}

// ---------------------------------------------------------------------------
// The plain language preview: "10% off all Starters, every day 3 to 6 pm".
// ---------------------------------------------------------------------------

/** The apps' translate function, loosely typed so staff-core needs no i18n. */
export type Translate = (key: string, values?: Record<string, string | number>) => string;

/** What an offer looks like, enough to describe it: a draft's input or a saved offer. */
export type DescribableOffer = Pick<
	Offer,
	| "kind"
	| "discountType"
	| "value"
	| "minOrderPaise"
	| "maxDiscountPaise"
	| "code"
	| "appliesTo"
	| "itemIds"
	| "categoryIds"
	| "startsAt"
	| "endsAt"
	| "daysOfWeek"
	| "timeFrom"
	| "timeTo"
>;

/** Names of the menu's dishes and categories, for the targets. */
export type OfferNames = {
	item: (id: string) => string | undefined;
	category: (id: string) => string | undefined;
};

const percentText = (bp: number) => hundredthsToText(bp);

/** "6 pm", "6:30 pm" */
function clock(time: string): string {
	const [h = 0, m = 0] = time.split(":").map(Number);
	const suffix = h < 12 ? "am" : "pm";
	const hour = h % 12 === 0 ? 12 : h % 12;
	return m === 0 ? `${hour} ${suffix}` : `${hour}:${String(m).padStart(2, "0")} ${suffix}`;
}

/** Names joined as "Starters, Mains and Breads"; more than three as "Starters and 4 more". */
function names(list: string[], t: Translate): string {
	if (list.length <= 1) return list[0] ?? "";
	if (list.length > 3)
		return t("offers:summary.andMore", { first: list[0] ?? "", count: list.length - 1 });
	return t("offers:summary.list", { items: list.slice(0, -1).join(", "), last: list.at(-1) ?? "" });
}

/**
 * The offer in one sentence, as owners read it in the form and the list
 * (`offers:summary.*`), such as "10% off all Starters, every day 3 to 6 pm".
 */
export function describeOffer(
	offer: DescribableOffer,
	lookup: OfferNames,
	t: Translate,
	timeZone: string,
): string {
	const amount = (paise: number) => formatPaise(paise);
	let what: string;
	if (offer.kind === "item_discount") {
		const target =
			offer.appliesTo === "all"
				? t("offers:summary.allDishes")
				: names(
						(offer.appliesTo === "items" ? offer.itemIds : offer.categoryIds).flatMap((id) => {
							const n = offer.appliesTo === "items" ? lookup.item(id) : lookup.category(id);
							return n ? [n] : [];
						}),
						t,
					);
		what =
			offer.discountType === "percent"
				? t("offers:summary.percentOn", { percent: percentText(offer.value), target })
				: offer.discountType === "flat"
					? t("offers:summary.flatOn", { amount: amount(offer.value), target })
					: t("offers:summary.fixedOn", { amount: amount(offer.value), target });
	} else {
		what =
			offer.discountType === "percent"
				? t("offers:summary.percentBill", { percent: percentText(offer.value) })
				: t("offers:summary.flatBill", { amount: amount(offer.value) });
		if (offer.minOrderPaise !== null)
			what = `${what} ${t("offers:summary.above", { amount: amount(offer.minOrderPaise) })}`;
	}
	if (offer.discountType === "percent" && offer.maxDiscountPaise !== null)
		what = `${what} ${t("offers:summary.upTo", { amount: amount(offer.maxDiscountPaise) })}`;
	if (offer.kind === "coupon" && offer.code)
		what = `${what} ${t("offers:summary.withCode", { code: offer.code })}`;

	const days =
		offer.daysOfWeek.length === 0
			? t("offers:summary.everyDay")
			: offer.daysOfWeek.length === 2 && offer.daysOfWeek[0] === 6 && offer.daysOfWeek[1] === 7
				? t("offers:summary.weekends")
				: offer.daysOfWeek.length === 5 && offer.daysOfWeek.every((d) => d <= 5)
					? t("offers:summary.weekdays")
					: names(
							offer.daysOfWeek.map((d) => t(`offers:days.${d}`)),
							t,
						);
	const hours =
		offer.timeFrom && offer.timeTo
			? ` ${t("offers:summary.hours", { from: clock(offer.timeFrom), to: clock(offer.timeTo) })}`
			: "";
	let when = `${days}${hours}`;
	if (offer.startsAt) {
		const start = localParts(new Date(offer.startsAt), timeZone).date;
		when = `${when}, ${t("offers:summary.from", { date: formatDay(start, t) })}`;
	}
	if (offer.endsAt) {
		const end = localParts(new Date(offer.endsAt), timeZone);
		// An end at midnight closes the day before.
		const last = end.time === "00:00" ? addDays(end.date, -1) : end.date;
		when = `${when}, ${t("offers:summary.until", { date: formatDay(last, t) })}`;
	}
	return t("offers:summary.sentence", { what, when });
}

/** "2026-10-01" → "1 Oct". */
export function formatDay(date: string, t: Translate): string {
	const [, m = "1", d = "1"] = date.split("-");
	return t("offers:summary.date", { day: Number(d), month: t(`offers:months.${Number(m)}`) });
}
